import { Switch } from "@xola/ui-kit";
import { getFields, getGuestQuestionnaireFields, initializeFieldResponse, shouldSkipQuestionnaireModal } from "common";
import { isEmpty, noop, pick } from "lodash";
import React, { useCallback, useEffect, useMemo } from "react";
import { Field, FieldType, FieldWithPickupOptions } from "types";
import { useDebouncedCallback } from "use-debounce";
import { initializeFormResponse } from "../GuestQuestionnaire/GuestQuestionnaireList";
import { createPurchaseStoreToPayload, updatePurchaseData, useUpdatePurchase } from "../PurchaseManager";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { useCustomerDetailsStore } from "@/components/PurchaseCreate/store/customerDetailsStore";
import { PickupLocationSelectField } from "@/components/Questionnaire/PickupLocationSelectField";
import { fieldMap, getInitialValues } from "@/components/Questionnaire/QuestionnaireModal";
import { TextareaField } from "@/components/Questionnaire/TextareaField";
import { TextField } from "@/components/Questionnaire/TextField";
import { isQuestionnaireSkipped } from "@/models/Form/Form";
import { getPurchaseItemGuests } from "@/models/PurchaseItem";
import { CreatePurchaseItemState, useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface FieldResponse {
    value: any;
    unit?: string;
    label?: string;
}

interface OrganizerQuestionnaireFieldProps {
    field: Field | FieldWithPickupOptions;
    index: number;
    response: FieldResponse;
    item: CreatePurchaseItemState;
    disableRequiredFormFields?: boolean;
    onChange: (value: any, field: Field | FieldWithPickupOptions) => void;
}

const OrganizerQuestionnaireField = React.memo(
    ({ field, index, response, item, disableRequiredFormFields, onChange }: OrganizerQuestionnaireFieldProps) => {
        // Debounce per field to prevent race conditions when fields change fast.
        const debouncedOnChange = useDebouncedCallback((value) => onChange(value, field), 300);

        if (field.type === FieldType.PICKUP) {
            return (
                <div data-testid="questionnaire-form-field" className="w-full">
                    <PickupLocationSelectField
                        isRequired={field.required && !disableRequiredFormFields}
                        item={item}
                        showDescription={false}
                        name={`formResponse.fields.${index}.value`}
                        field={{ ...field, ...pick(response, ["unit"]) } as FieldWithPickupOptions}
                        classNames={{ label: "overflow-hidden text-ellipsis no-translate" }}
                        onChange={debouncedOnChange}
                    />
                </div>
            );
        }

        let Input = fieldMap[field.type] ?? TextField;
        if (fieldMap[field.type] === TextField && response.value?.length > 60) {
            Input = TextareaField;
        }

        return (
            <div data-testid="questionnaire-form-field" className="w-full">
                <Input
                    isRequired={field.required && !disableRequiredFormFields}
                    showDescription={false}
                    name={`formResponse.fields.${index}.value`}
                    field={{ ...field, ...pick(response, ["unit"]) } as Field}
                    classNames={{ label: "overflow-hidden text-ellipsis no-translate" }}
                    onChange={debouncedOnChange}
                />
            </div>
        );
    },
);

export const OrganizerQuestionnaireList = ({
    label,
    isAttendeeUnique,
}: {
    label?: string;
    isAttendeeUnique?: boolean;
}) => {
    const { mutateAsync: updatePurchase } = useUpdatePurchase();

    const form = useCreatePurchaseStore((state) => state.getForm());
    const formResponse = useCreatePurchaseStore((state) => state.getActiveItem().formResponse);
    const guestsFromStore = useCreatePurchaseStore((state) => state.getActiveItem().guests);
    const organizer = useCreatePurchaseStore((state) => state.organizer);
    const item = useCreatePurchaseStore((state) => state.getActiveItem());

    const { activeItemIndex } = useCreatePurchaseStore.getState();

    const disableRequiredFormFields = useCreatePurchaseStore(
        (state) => state.getActiveItem().disableRequiredFormFields,
    );

    const guests = useMemo(
        () => getPurchaseItemGuests(guestsFromStore, form, formResponse),
        [guestsFromStore, form, formResponse],
    );
    const primaryGuest = useMemo(() => guests.find((guest) => guest.primary) ?? guests[0], [guests]);

    const questionnaireFields = useMemo(() => {
        if (!form) {
            return [];
        }

        // Equipment purchases with "Collect demographic information" off have no attendee guests, but the
        // questionnaire is still collected at the item level, so fall back to the raw form fields.
        const fields = primaryGuest
            ? (getGuestQuestionnaireFields(form, primaryGuest, true, true) as (Field | FieldWithPickupOptions)[])
            : (getFields(form, true, true) as (Field | FieldWithPickupOptions)[]);

        return fields.filter((field: Field | FieldWithPickupOptions) =>
            isAttendeeUnique ? shouldSkipQuestionnaireModal(field as Field) : true,
        );
    }, [form, isAttendeeUnique, primaryGuest]);

    const handleDisableRequiredFieldsChange = (disableRequiredFields: boolean) => {
        const { activeItemIndex, actions } = useCreatePurchaseStore.getState();

        actions.setPurchase((draft) => {
            draft.items[activeItemIndex].disableRequiredFormFields = disableRequiredFields;
        });
    };

    const shouldToggleIgnoreValidations = disableRequiredFormFields === undefined && isQuestionnaireSkipped(form);

    useEffect(() => {
        // If optionalSourcesList contains "xola", we need to toggle on the "Ignore questionnaire validations" by
        // default
        if (shouldToggleIgnoreValidations) {
            handleDisableRequiredFieldsChange(true);
        }
    }, [shouldToggleIgnoreValidations]);

    const formResponseTemplate = useMemo(() => {
        if (!form) return null;
        if (primaryGuest) {
            return initializeFormResponse(form, true, primaryGuest, organizer);
        }
        // No attendee guest (equipment + collect demographic off): initialize blank item-level fields.
        return { fields: questionnaireFields.map((field) => initializeFieldResponse(field as Field)) };
    }, [form, primaryGuest, organizer, questionnaireFields]);

    const handleGuestChange = useCallback(
        async (value: string, field: any) => {
            if (!formResponseTemplate) return;

            if (field.organizerOnly) {
                useCreatePurchaseStore.getState().actions.setPurchase((draft) => {
                    const itemIndex = activeItemIndex;
                    if (!draft.items[itemIndex]) return;

                    const existingFields = draft.items[itemIndex]?.formResponse?.fields ?? [];
                    const newField = { ...initializeFieldResponse(field), value };

                    const fieldIndex = existingFields.findIndex((f) => f.label === newField.label);

                    const updatedFields =
                        fieldIndex !== -1
                            ? existingFields.map((field, index) => (index === fieldIndex ? newField : field))
                            : [...existingFields, newField];

                    draft.items[itemIndex].formResponse = {
                        ...draft.items[itemIndex]?.formResponse,
                        fields: updatedFields,
                        name: useCustomerDetailsStore.getState().customerName,
                    };
                });

                if (field.type === FieldType.PICKUP) {
                    const payload = createPurchaseStoreToPayload(useCreatePurchaseStore.getState().purchase);
                    useCreatePurchaseStore.setState({ isProcessing: true });
                    const newPurchase = await updatePurchase(payload);
                    updatePurchaseData(newPurchase);
                }
            }
        },
        [formResponseTemplate, updatePurchase, activeItemIndex],
    );

    useEffect(() => {
        if (!form || !questionnaireFields || questionnaireFields.length === 0 || formResponse) {
            return;
        }

        const { activeItemIndex, actions } = useCreatePurchaseStore.getState();
        const currentItem = useCreatePurchaseStore.getState().purchase.items[activeItemIndex];

        if (!currentItem.formResponse?.fields) {
            const initializedFields = questionnaireFields.map((field: Field | FieldWithPickupOptions) =>
                initializeFieldResponse(field as Field),
            );

            actions.setPurchase((draft) => {
                const itemIndex = activeItemIndex;
                if (!draft.items[itemIndex]) return;

                draft.items[itemIndex].formResponse = {
                    ...draft.items[itemIndex]?.formResponse,
                    fields: initializedFields,
                    name: useCustomerDetailsStore.getState().customerName,
                };
            });
        }
    }, [form, questionnaireFields, formResponse, activeItemIndex, primaryGuest, isAttendeeUnique]);

    if (!form || !questionnaireFields || questionnaireFields.length === 0) {
        return null;
    }

    return (
        <div>
            <div className="mb-4 flex flex-col items-start">
                <div className="mb-4 flex">
                    <h3 className="mr-4">{label ?? "Questionnaire"}</h3>
                    {form && !isEmpty(form.fields) && (
                        <Switch.Group>
                            <Switch
                                isChecked={disableRequiredFormFields}
                                size="small"
                                data-testid="guest-questionnaire-list-disable-required-fields-checkbox"
                                onChange={handleDisableRequiredFieldsChange}
                            />

                            <Switch.Label className="ml-2">Ignore questionnaire for now</Switch.Label>
                        </Switch.Group>
                    )}
                </div>

                <XolaFormik
                    initialValues={getInitialValues(form, primaryGuest, undefined, item, true) ?? {}}
                    onSubmit={noop}
                >
                    {questionnaireFields.map((field: Field | FieldWithPickupOptions, index: number) => {
                        if (isAttendeeUnique && !shouldSkipQuestionnaireModal(field as Field)) {
                            // If the marketing consent field is organizer only then we don't show it here
                            return null;
                        }

                        // Responses for the organizer questionnaire are stored at the item level
                        // (item.formResponse), so read the saved value from there.
                        const response = formResponse?.fields?.find((responseField: FieldResponse) => {
                            return field.label === responseField.label;
                        }) ?? { value: "" };

                        return (
                            <OrganizerQuestionnaireField
                                key={field.id}
                                field={field}
                                index={index}
                                response={response}
                                item={item}
                                disableRequiredFormFields={disableRequiredFormFields}
                                onChange={handleGuestChange}
                            />
                        );
                    })}
                </XolaFormik>
            </div>

            <hr className="!mt-4 !mb-8 text-gray-light" />
        </div>
    );
};
